<template>
    <div v-if="isV2">
        <div v-if="cardMode" class="card bg-base-100 border border-base-200">
            <button class="card-body p-4 flex-row items-center justify-between text-left" type="button" :disabled="isLoading" @click="open">
                <div>
                    <div class="font-medium">{{ $tt('快捷指令') }}</div>
                    <div class="text-sm text-base-content/70">{{ $tt('使用预设批量处理已选文件') }}</div>
                </div>
                <span class="text-lg">⌘</span>
            </button>
        </div>
        <div v-else-if="showTrigger" class="mt-2">
            <button :class="['btn btn-sm btn-ghost drawer-button hover:bg-neutral hover:text-neutral-content', !detail ? 'tooltip tooltip-top' : '']"
                :data-tip="$tt('快捷指令')" :disabled="isLoading" type="button" @click="open">
                <span :class="['flex items-center', detail ? 'w-40' : '']">
                    <span class="w-16 text-center text-lg">⌘</span>
                    <span v-if="detail" class="mr-auto">{{ $tt('快捷指令') }}</span>
                </span>
            </button>
        </div>

        <div class="drawer drawer-end">
            <input id="operation-preset-runner" v-model="visible" type="checkbox" class="drawer-toggle" />
            <div class="drawer-side">
                <label for="operation-preset-runner" class="drawer-overlay" @click="visible = false"></label>
                <div class="preset-runner">
                    <header class="preset-runner__header">
                        <div>
                            <h2>{{ $tt('快捷指令') }}</h2>
                            <p>{{ $tt('先预览变更，再确认执行。') }}</p>
                        </div>
                        <button class="btn btn-ghost btn-sm btn-circle" type="button" @click="visible = false">×</button>
                    </header>
                    <main class="preset-runner__body">
                        <div v-if="loadingPresets" class="preset-runner__empty">{{ $tt('正在加载快捷指令...') }}</div>
                        <template v-else>
                            <label class="form-control">
                                <span class="label-text">{{ $tt('搜索指令') }}</span>
                                <input v-model.trim="presetQuery" class="input input-bordered input-sm" type="search" :placeholder="$tt('输入名称或描述')" />
                            </label>
                            <label class="form-control">
                                <span class="label-text">{{ $tt('选择指令') }}</span>
                                <select v-model="selectedId" class="select select-bordered select-sm">
                                    <option value="">{{ $tt('请选择快捷指令') }}</option>
                                    <option v-for="preset in filteredActivePresets" :key="preset.id" :value="preset.id">
                                        {{ preset.display_name }} · {{ riskLabel(preset.risk_level) }} · {{ preset.step_count }} {{ $tt('步骤') }}
                                    </option>
                                </select>
                            </label>
                            <div v-if="!filteredActivePresets.length" class="preset-runner__empty preset-runner__empty--compact">
                                {{ $tt('没有匹配的快捷指令') }}
                            </div>
                            <section v-if="selectedPreset" class="preset-runner__summary">
                                <div class="preset-runner__summary-top">
                                    <strong>{{ selectedPreset.display_name }}</strong>
                                </div>
                                <p>{{ selectedPreset.description || $tt('暂无描述') }}</p>
                                <div>
                                    <span v-for="(step, index) in selectedPreset.definition.steps" :key="index">{{ operationLabel(step.operation) }}</span>
                                </div>
                            </section>
                            <label v-if="requiresRiskAcknowledgement" class="preset-runner__risk-confirm">
                                <input v-model="riskAcknowledged" type="checkbox" class="checkbox checkbox-warning checkbox-sm" />
                                <span>{{ $tt('我已了解该指令会修改文件路径或批量写入标签。') }}</span>
                            </label>
                            <section v-if="runtimeSchema.length" class="preset-runner__runtime">
                                <div class="preset-runner__runtime-title">
                                    <strong>{{ $tt('运行参数') }}</strong>
                                    <span>{{ $tt('请填写本次执行使用的值') }}</span>
                                </div>
                                <label v-for="field in runtimeSchema" :key="field.key" class="form-control">
                                    <span class="label-text">
                                        {{ field.label || field.key }}
                                        <small v-if="field.required">{{ $tt('必填') }}</small>
                                    </span>
                                    <span v-if="field.description" class="preset-runner__runtime-help">{{ field.description }}</span>
                                    <input
                                        v-if="field.type === 'string' || field.type === 'path' || field.type === 'number'"
                                        v-model="runtimeParams[field.key]"
                                        class="input input-bordered input-sm"
                                        :type="field.type === 'number' ? 'number' : 'text'"
                                    />
                                    <input
                                        v-else-if="field.type === 'boolean'"
                                        v-model="runtimeParams[field.key]"
                                        type="checkbox"
                                        class="toggle toggle-primary"
                                    />
                                    <select v-else v-model="runtimeParams[field.key]" class="select select-bordered select-sm">
                                        <option v-for="option in field.options || []" :key="String(option)" :value="option">
                                            {{ option }}
                                        </option>
                                    </select>
                                </label>
                            </section>
                            <button class="btn btn-primary btn-sm w-full" type="button"
                                :disabled="!selectedPreset || previewing || !hasSelection || !runtimeReady" @click="preview">
                                <span v-if="previewing" class="loading loading-spinner loading-xs"></span>
                                {{ $tt('生成预览') }}
                            </button>
                            <div v-if="!hasSelection" class="alert alert-warning text-sm">{{ $tt('请先选择文件或文件夹。') }}</div>
                            <section v-if="previewData" class="preset-runner__preview">
                                <div class="preset-runner__preview-head">
                                    <strong>{{ $tt('变更预览') }}</strong>
                                    <span>{{ $tt('共 {count} 项', { count: previewData.total_count }) }}</span>
                                </div>
                                <div class="preset-runner__counts">
                                    <span>{{ $tt('预计修改') }} {{ previewData.changed_count }}</span>
                                    <span>{{ $tt('可执行') }} {{ previewData.ready_count }}</span>
                                    <span>{{ $tt('跳过') }} {{ previewData.skipped_count }}</span>
                                    <span>{{ $tt('冲突') }} {{ previewData.conflict_count }}</span>
                                    <span>{{ $tt('无效') }} {{ previewData.invalid_count }}</span>
                                </div>
                                <div v-for="item in previewData.items" :key="item.path" class="preset-runner__preview-item" :class="item.status">
                                    <div>{{ item.path }}</div>
                                    <div v-if="item.target_path !== item.path">{{ item.target_path }}</div>
                                    <small v-if="item.reason">{{ item.reason }}</small>
                                    <details v-if="item.steps && item.steps.length">
                                        <summary>{{ $tt('查看步骤变更') }}</summary>
                                        <div v-for="step in item.steps" :key="step.step" class="preset-runner__step-change">
                                            <span>{{ step.step }}. {{ operationLabel(step.operation) }}</span>
                                            <code>{{ formatChanges(step.changes) }}</code>
                                        </div>
                                    </details>
                                </div>
                                <div v-for="item in previewData.failures" :key="item.path" class="preset-runner__preview-item invalid">
                                    <div>{{ item.path }}</div>
                                    <small>{{ item.reason }}</small>
                                </div>
                            </section>
                        </template>
                    </main>
                    <footer class="preset-runner__footer">
                        <button class="btn btn-outline btn-sm" type="button" @click="visible = false">{{ $tt('取消') }}</button>
                        <button class="btn btn-primary btn-sm" type="button"
                            :disabled="!previewData || previewData.ready_count === 0 || executing || (requiresRiskAcknowledgement && !riskAcknowledged)" @click="execute">
                            <span v-if="executing" class="loading loading-spinner loading-xs"></span>
                            {{ $tt('确认执行') }}
                        </button>
                    </footer>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'OperationPresetRunner',
    props: {
        detail: { type: Boolean, default: false },
        isLoading: { type: Boolean, default: false },
        checkedData: { type: Array, default: () => [] },
        filePath: { type: String, default: '' },
        userRole: { type: String, default: '' },
        cardMode: { type: Boolean, default: false },
        showTrigger: { type: Boolean, default: true }
    },
    data() {
        return {
            visible: false,
            presets: [],
            operations: [],
            loadingPresets: false,
            presetQuery: '',
            selectedId: '',
            runtimeParams: {},
            previewData: null,
            previewing: false,
            executing: false,
            riskAcknowledged: false
        }
    },
    computed: {
        isV2() {
            return String(this.userRole || '').toUpperCase() === 'V2'
        },
        activePresets() {
            return this.presets.filter(item => item.status === 'active')
        },
        filteredActivePresets() {
            const query = this.presetQuery.toLocaleLowerCase()
            if (!query) return this.activePresets
            return this.activePresets.filter(item => {
                const text = [item.display_name, item.description, item.risk_level]
                    .filter(Boolean)
                    .join(' ')
                    .toLocaleLowerCase()
                return text.includes(query)
            })
        },
        selectedPreset() {
            return this.activePresets.find(item => String(item.id) === String(this.selectedId)) || null
        },
        runtimeSchema() {
            return this.selectedPreset?.input_schema || []
        },
        runtimeReady() {
            return this.runtimeSchema.every(field => {
                if (!field.required) return true
                const value = this.runtimeParams[field.key]
                return value !== undefined && value !== null && value !== ''
            })
        },
        requiresRiskAcknowledgement() {
            return this.selectedPreset && ['medium', 'high'].includes(this.selectedPreset.risk_level)
        },
        hasSelection() {
            return Array.isArray(this.checkedData) && this.checkedData.length > 0
        }
    },
    watch: {
        selectedId() {
            this.previewData = null
            this.riskAcknowledged = false
            this.initializeRuntimeParams()
        }
    },
    methods: {
        async open(preset = null) {
            if (!this.hasSelection) {
                this.notify('发生提示', '请先勾选文件或文件夹！', 'warning')
                return
            }
            this.visible = true
            if (!this.presets.length) await this.load()
            if (preset) {
                this.presetQuery = ''
                const nextId = typeof preset === 'object' ? preset.id : preset
                if (String(this.selectedId) === String(nextId)) {
                    this.previewData = null
                    this.riskAcknowledged = false
                    this.initializeRuntimeParams()
                } else {
                    this.selectedId = nextId
                }
            }
        },
        openPresetDrawer(preset = null) { return this.open(preset) },
        closePresetDrawer() { this.visible = false },
        isPresetDrawerOpen() { return this.visible },
        async load() {
            this.loadingPresets = true
            try {
                const [presetResponse, operationResponse] = await Promise.all([
                    this.$api.scriptMgmt.getOperationPresets({ page_size: 100 }),
                    this.$api.scriptMgmt.getOperationDefinitions()
                ])
                this.presets = presetResponse.data.items || presetResponse.data || []
                this.operations = operationResponse.data || []
            } catch (error) {
                this.notify('获取快捷指令失败', error.message, 'error')
            } finally {
                this.loadingPresets = false
            }
        },
        operationLabel(id) {
            return this.operations.find(item => item.id === id)?.display_name || id
        },
        riskLabel(level) {
            return { low: '低风险', medium: '中风险', high: '高风险' }[level] || level
        },
        formatChanges(changes) {
            return JSON.stringify(changes || {})
        },
        initializeRuntimeParams() {
            const params = {}
            this.runtimeSchema.forEach(field => {
                if (field.type === 'boolean') {
                    params[field.key] = field.default === true || field.default === 'true' || field.default === 1 || field.default === '1'
                } else if (field.type === 'number') {
                    params[field.key] = field.default !== undefined && field.default !== null && field.default !== ''
                        ? Number(field.default)
                        : ''
                } else if (field.type === 'select' && Array.isArray(field.options) && field.options.length) {
                    params[field.key] = field.default !== undefined && field.default !== null && field.default !== ''
                        ? field.default
                        : field.options[0]
                } else {
                    params[field.key] = field.default !== undefined && field.default !== null
                        ? field.default
                        : ''
                }
            })
            this.runtimeParams = params
        },
        async preview() {
            this.previewing = true
            try {
                const response = await this.$api.scriptMgmt.previewOperationPreset(this.selectedPreset.id, {
                    file_full_path: this.filePath,
                    select_data: this.checkedData,
                    runtime_params: this.runtimeParams
                })
                if (!response.result) throw new Error(response.message || '预览失败')
                this.previewData = response.data
            } catch (error) {
                this.notify('预览失败', error.message, 'error')
            } finally {
                this.previewing = false
            }
        },
        async execute() {
            this.executing = true
            this.$emit('loading-start')
            try {
                const response = await this.$api.scriptMgmt.executeOperationPreset(this.selectedPreset.id, {
                    file_full_path: this.filePath,
                    select_data: this.checkedData,
                    runtime_params: this.runtimeParams,
                    confirmed: true,
                    risk_acknowledged: this.riskAcknowledged
                })
                if (!response.result) throw new Error(response.message || '执行失败')
                this.notify('快捷指令任务已创建', '', 'success')
                this.visible = false
                this.previewData = null
                this.$parent.handleSearchFile && this.$parent.handleSearchFile()
            } catch (error) {
                this.notify('执行失败', error.message, 'error')
            } finally {
                this.executing = false
                this.$emit('loading-end')
            }
        },
        notify(title, content, type) {
            this.$myMsg.notify({ title: this.$tt(title), content, type, time: 3000 })
        }
    }
}
</script>

<style scoped>
.preset-runner { display: flex; width: min(100vw, 480px); min-height: 100%; flex-direction: column; color: oklch(var(--bc)); background: oklch(var(--b1)); }
.preset-runner__header, .preset-runner__footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 16px; border-bottom: 1px solid oklch(var(--bc) / .1); }
.preset-runner__header h2 { margin: 0; font-size: 20px; font-weight: 800; }.preset-runner__header p { margin: 4px 0 0; color: oklch(var(--bc) / .58); font-size: 12px; }
.preset-runner__body { display: grid; flex: 1; align-content: start; gap: 14px; padding: 16px; overflow-y: auto; }.preset-runner__footer { border-top: 1px solid oklch(var(--bc) / .1); border-bottom: 0; }
.preset-runner__summary, .preset-runner__preview { padding: 12px; border: 1px solid oklch(var(--bc) / .1); border-radius: 9px; background: oklch(var(--b2) / .58); }
.preset-runner__runtime { display: grid; gap: 10px; padding: 12px; border: 1px solid oklch(var(--bc) / .1); border-radius: 9px; background: oklch(var(--b2) / .38); }
.preset-runner__risk-confirm { display: flex; align-items: flex-start; gap: 8px; padding: 10px 12px; border: 1px solid oklch(var(--a) / .32); border-radius: 8px; color: oklch(var(--bc) / .78); background: oklch(var(--a) / .08); font-size: 12px; line-height: 1.5; }
.preset-runner__runtime-title { display: flex; justify-content: space-between; gap: 8px; align-items: baseline; }.preset-runner__runtime-title span, .preset-runner__runtime-help { color: oklch(var(--bc) / .55); font-size: 11px; }.preset-runner__runtime .label-text { display: flex; gap: 5px; align-items: center; padding-bottom: 5px; font-size: 12px; }.preset-runner__runtime .label-text small { color: oklch(var(--er)); font-size: 10px; }
.preset-runner__summary strong { font-size: 14px; }.preset-runner__summary p { margin: 6px 0; color: oklch(var(--bc) / .62); font-size: 12px; }.preset-runner__summary div { display: flex; flex-wrap: wrap; gap: 6px; }.preset-runner__summary span { padding: 3px 6px; border-radius: 5px; color: oklch(var(--bc) / .72); background: oklch(var(--bc) / .06); font-size: 11px; }
.preset-runner__summary-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.preset-runner__summary-top span { font-weight: 700; }
.preset-runner__preview-head, .preset-runner__counts { display: flex; justify-content: space-between; gap: 8px; }.preset-runner__preview-head span, .preset-runner__counts { color: oklch(var(--bc) / .6); font-size: 12px; }.preset-runner__counts { justify-content: flex-start; margin: 8px 0; }
.preset-runner__preview-item { display: grid; gap: 3px; padding: 8px 0; border-top: 1px solid oklch(var(--bc) / .08); color: oklch(var(--bc) / .72); font-size: 11px; word-break: break-all; }.preset-runner__preview-item div + div { color: oklch(var(--su)); }.preset-runner__preview-item.conflict, .preset-runner__preview-item.invalid { color: oklch(var(--er)); }.preset-runner__preview-item small { color: inherit; }.preset-runner__empty { padding: 30px 0; color: oklch(var(--bc) / .58); text-align: center; }
.preset-runner__preview-item details { color: oklch(var(--bc) / .62); }.preset-runner__step-change { display: grid; gap: 2px; margin-top: 5px; }.preset-runner__step-change code { white-space: pre-wrap; word-break: break-word; }
.preset-runner__empty--compact { padding: 10px 0; font-size: 12px; }
.risk-medium { color: oklch(var(--a)) !important; }.risk-high { color: oklch(var(--er)) !important; }.risk-low { color: oklch(var(--su)) !important; }
</style>
